/**
 * Turns a pending interact press — or a tap on an object — into a game event: open or
 * close the targeted door, pick up the targeted box, flip the targeted switch, fit the
 * carried part to the thing it is a part of. Runs the targeting ray, then consumes the
 * flags.
 *
 * With the hands full the press is normally `CarryController`'s, as drop. The one
 * exception is attaching, and the two systems agree on which frame that is by asking the
 * same pure question — `availableAction()` on the same store state, in the same frame —
 * rather than by mount order (design D4). Whoever declines consumes nothing, so there is
 * still exactly one consumer of a press per frame.
 *
 * A press has no position, so it acts on whatever the crosshair rests on. A tap has one,
 * so it acts on what was tapped: on a pad the thumb is the aim.
 */

import { useFrame } from '@react-three/fiber';
import { useRapier } from '@react-three/rapier';
import { availableAction } from '../game/interaction';
import { isPlayable } from '../game/phase';
import { consumeInteract, consumeTap, consumeThrow, inputState } from '../input/inputState';
import type { ObjectKind } from '../game/profile/types';
import { useGameStore } from '../store/gameStore';
import { getObjectBody } from './objectRegistry';
import { parkBody } from './parkBody';
import { useInteractionTarget, useTapTarget } from './useInteractionTarget';

export function InteractionSystem(): null {
  useInteractionTarget();
  const targetAtTap = useTapTarget();
  const { rapier } = useRapier();

  /**
   * Fitting the carried part to its host: the physics here, the rule in the store. The
   * part's body is parked and switched off exactly as a stow parks it (design D7) — it
   * stays mounted and registered, and the host draws it at its socket — and then the
   * store decides which socket it went into and whether that finished the job. The
   * engine never picks the socket.
   */
  function attach(
    store: ReturnType<typeof useGameStore.getState>,
    hostId: string,
    hostKind: ObjectKind,
  ): void {
    const partId = store.carriedObjectId;
    if (partId === null) return;
    const body = getObjectBody(partId);
    // No body means the level the part belongs to is gone; report no attachment rather
    // than an assembly with an invisible leg on it.
    if (!body) return;

    // The store decides first, the physics follows. `attachPart` re-checks the fit
    // against its own current state and declines silently if it no longer holds; parking
    // a body the store then refused would leave the part switched off in the player's
    // hands, invisible and unthrowable.
    store.attachPart(hostId, hostKind);
    if (useGameStore.getState().attachments[partId] === undefined) return;
    parkBody(body, rapier.RigidBodyType.Dynamic);
  }

  useFrame(() => {
    const store = useGameStore.getState();

    // Consumed before any early return: a tap is a moment, not a queue. Left pending, a
    // tap made while paused or while carrying would fire the instant play resumes or the
    // box goes down, acting on whatever happens to be in front of the player then.
    const tap = consumeTap(inputState);

    if (!isPlayable(store.phase)) return;

    const context = {
      carriedObjectId: store.carriedObjectId,
      carriedObjectKind: store.carriedObjectKind,
      doorStates: store.doorStates,
      roomLights: store.roomLights,
      attachments: store.attachments,
    };

    if (store.carriedObjectId !== null) {
      // Hands full: the press is `CarryController`'s, as drop — unless the pure decider
      // says it is an attach. Both systems ask the same question of the same store state
      // in the same frame, so `useFrame` mount order cannot decide the answer (design
      // D4), and nothing is consumed on the way to finding out: a press swallowed here
      // and then declined is a drop that never happens.
      const held = (tap && targetAtTap(tap)) ?? store.focus;
      const action = availableAction(held, context);
      const hostKind = held?.objectKind;
      if (action?.verb !== 'attach' || !hostKind) return;

      // Both flags go, whether or not this frame carries a press. A desktop left-click
      // sets throw *and* interact, because the producer cannot know what the hands hold;
      // a throw left pending would hurl the part at its host a frame after it was fitted,
      // or the instant the player looked away. Aiming at the dog means a click attaches.
      consumeThrow(inputState);
      const pressedAtHost = consumeInteract(inputState);
      if (!pressedAtHost && !tap) return;

      attach(store, action.targetId, hostKind);
      return;
    }

    // Throw means nothing with empty hands — discard it, or a click made while
    // exploring stays pending and fires the instant a box is picked up, releasing it
    // on the spot instead of carrying it.
    consumeThrow(inputState);

    const pressed = consumeInteract(inputState);
    if (!pressed && !tap) return;

    // A tap that landed on something wins; otherwise a press falls back to the crosshair.
    // A tap on empty air is deliberately not a crosshair press — the player aimed, and
    // missed, and acting on something else entirely would be worse than doing nothing.
    const target = (tap && targetAtTap(tap)) ?? (pressed ? store.focus : null);

    const action = availableAction(target, context);
    // Interacting at nothing does nothing — no error, no sound, no state change.
    if (!action) return;

    if (action.verb === 'open' || action.verb === 'close') store.toggleDoor(action.targetId);
    else if (action.verb === 'pick-up') store.pickUp(action.targetId, target?.objectKind ?? null);
    else if (action.verb === 'switch-on' || action.verb === 'switch-off') {
      // The target id of a switch is the room it lights.
      store.toggleRoomLight(action.targetId);
    }
  });

  return null;
}
